import type {
  TileStreamingConfig,
  TileStreamingSelectionProfile,
} from "../world/streaming/index.ts";

/**
 * Re-evaluate metric LOD promotion during normal travel instead of waiting for
 * the player to cross a complete 256 m tile. Eight metres keeps the 96 m detail
 * envelope current without running manifest selection every simulation tick.
 */
export const TILE_STREAMING_LOD_REFRESH_DISTANCE_M = 8;

/**
 * M2 streaming budget. These are capacity choices, not movement-feel tuning.
 *
 * A one-tile Chebyshev ring keeps every visible fog-range neighbour available.
 * The nine-tile cache holds that complete ring and still forces eviction during
 * a representative traversal of the 4 × 4 development area.
 */
export const TILE_STREAMING_CONFIG: Readonly<TileStreamingConfig> =
  Object.freeze({
    maxConcurrentLoads: 1,
    maxCachedTiles: 9,
    // Keeps cross-boundary façades and roofs detailed within the near camera
    // envelope instead of exposing whole-footprint LOD boxes beside the player.
    fullDetailRadiusM: 96,
    lodRings: Object.freeze({
      fullRadiusTiles: 0,
      simplifiedRadiusTiles: 1,
      distantRadiusTiles: 1,
    }),
  });

/**
 * Flight is an explicit development overview mode. A five-tile Chebyshev ring
 * is 11 × 11 at an interior world position, so its cache must hold all 121
 * selected cells now that the runtime envelope extends beyond the original
 * 20-tile development AOI. Nearby tiles retain the walking LODs while the
 * outer rings use the distant, non-colliding view. This covers the expanded
 * aerial camera/fog envelope without changing normal ground streaming.
 */
export const TILE_STREAMING_FLIGHT_PROFILE:
  Readonly<TileStreamingSelectionProfile> = Object.freeze({
    maxCachedTiles: 121,
    fullDetailRadiusM: TILE_STREAMING_CONFIG.fullDetailRadiusM,
    lodRings: Object.freeze({
      fullRadiusTiles: 0,
      simplifiedRadiusTiles: 1,
      distantRadiusTiles: 5,
    }),
  });
